import React, {
  useState,
  useEffect,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  addForm,
  editForm,
} from '@pingum/app-state-management/form/actions';
import PropTypes from 'prop-types';
import {
  makeStyles,
  withMobileDialog,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@material-ui/core';

import FormInformation from './components/FormInformation';
import FormEvents from './components/FormEvents';
import FormEventData from './components/FormEventData';
import FormReview from './components/FormReview';
import styles from './styles';

const useStyles = makeStyles(styles);

const getDefaultRequest = (form, tenantColor) => ({
  id: form
    ? form.id
    : null,
  title: (form && form.title)
    ? form.title
    : '',
  description: (form && form.description)
    ? form.description
    : 'Please fill in the form below and press submit.',
  type: (form && form.type)
    ? form.type
    : 'Single',
  eventTemplates: form
    ? form.eventTemplates
    : [],
  hiddenEventDataIds: (form && form.hiddenEventDataIds)
    ? form.hiddenEventDataIds
    : [],
  formEventDataIds: (form && form.formEventDataIds)
    ? form.formEventDataIds
    : [],
  submitButtonText: (form && form.submitButtonText)
    ? form.submitButtonText
    : 'Submit',
  submitLabel: (form && form.submitLabel)
    ? form.submitLabel
    : 'By submitting this form, you acknowledge that you have provided all necessary information in the fields above.',
  successMessage: (form && form.successMessage)
    ? form.successMessage
    : 'The form has been successfully submitted.',
  errorMessage: (form && form.errorMessage)
    ? form.errorMessage
    : 'There was an error submitting this form. Please contact support@pingum.io for help.',
  primaryColor: (form && form.primaryColor)
    ? form.primaryColor
    : tenantColor,
  validation: {
    title: {
      hasChanged: false,
      isValid: !!form,
      message: 'Please provide a valid title (between 2 and 100 characters in length).',
    },
    description: {
      isValid: true,
      message: 'Please provide a valid description (between 0 and 1024 characters in length).',
    },
    eventTemplates: {
      hasChanged: false,
      isValid: !!form,
      message: 'Please select workflow(s) for this form.',
    },
    submitButtonText: {
      isValid: true,
      message: 'Please provide a valid submit button text (between 0 and 100 characters in length).',
    },
    submitLabel: {
      isValid: true,
      message: 'Please provide a valid submit label (between 0 and 1024 characters in length).',
    },
    successMessage: {
      isValid: true,
      message: 'Please provide a valid form submission success message (between 0 and 1024 characters in length).',
    },
    errorMessage: {
      isValid: true,
      message: 'Please provide a valid form submission error message (between 0 and 1024 characters in length).',
    },
  },
});

const getSteps = ({
  formRequest,
  setFormRequest,
}) => [{
  label: 'Setup Form Information',
  content: (
    <FormInformation
      formRequest={formRequest}
      setFormRequest={setFormRequest}
    />
  ),
  optional: false,
  validate: () => {
    setFormRequest({
      ...formRequest,
      validation: {
        ...formRequest.validation,
        title: {
          ...formRequest.validation.title,
          hasChanged: true,
        },
      },
    });
    return (
      formRequest.validation.title.isValid
    && formRequest.validation.description.isValid
    && formRequest.validation.submitLabel.isValid
    && formRequest.validation.submitButtonText.isValid
    && formRequest.validation.successMessage.isValid
    && formRequest.validation.errorMessage.isValid
    );
  },
  finishedPreview: () => formRequest.title,
}, {
  label: 'Set Form Type and Events',
  content: (
    <FormEvents
      formRequest={formRequest}
      setFormRequest={setFormRequest}
    />
  ),
  optional: false,
  validate: () => {
    setFormRequest({
      ...formRequest,
      validation: {
        ...formRequest.validation,
        eventTemplates: {
          ...formRequest.validation.eventTemplates,
          hasChanged: true,
        },
      },
    });
    return (formRequest.validation.eventTemplates.isValid);
  },
  finishedPreview: () => {
    const eventTemplates = formRequest.eventTemplates.length > 0
      ? formRequest.eventTemplates
      : [{ name: '' }];
    return `${formRequest.type} Form - ${
      formRequest.type === 'Single'
        ? eventTemplates[0].name
        : `${eventTemplates.length} Workflows`
    }`;
  },
}, {
  label: 'Form Custom Fields',
  content: (
    <FormEventData
      formRequest={formRequest}
      setFormRequest={setFormRequest}
    />
  ),
  optional: true,
  validate: () => true,
  finishedPreview: () => {
    const eventTemplates = formRequest.eventTemplates.length > 0
      ? formRequest.eventTemplates
      : [{ name: '' }];
    return `${formRequest.type} Form - ${
      formRequest.type === 'Single'
        ? eventTemplates[0].name
        : `${eventTemplates.length} Workflows`
    }`;
  },
}, {
  label: 'Disclaimer and Confirm',
  content: <FormReview formRequest={formRequest} />,
  validate: () => true,
  finishedPreview: () => '',
}];

export const AddEditFormModalComponent = (props) => {
  const {
    form,
    open,
    onClose,
    fullScreen,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  // redux state
  const {
    isAddingForm,
    isEditingForm,
  } = useSelector((state) => state.form);
  // redux state
  const { tenant } = useSelector((state) => state.tenant);
  // local state
  const [formRequest, setFormRequest] = useState(getDefaultRequest(form, tenant.iconColor));
  const [activeStep, setActiveStep] = useState(0);

  // when passed in condition or open state changes, reset form
  useEffect(() => {
    setFormRequest(getDefaultRequest(form, tenant.iconColor));
    setActiveStep(0);
  }, [form, open]);

  const onSaveForm = () => {
    const request = {
      title: formRequest.title,
      description: formRequest.description,
      type: formRequest.type,
      eventTemplateIds: formRequest.eventTemplates.map((et) => et.id),
      formEventDataIds: formRequest.formEventDataIds,
      hiddenEventDataIds: formRequest.hiddenEventDataIds,
      submitLabel: formRequest.submitLabel,
      submitButtonText: formRequest.submitButtonText,
      successMessage: formRequest.successMessage,
      errorMessage: formRequest.errorMessage,
      primaryColor: formRequest.primaryColor,
    };
    if (formRequest.id) {
      dispatch(editForm(formRequest.id, request)).then(() => {
        onClose();
      });
    } else {
      dispatch(addForm(request)).then(() => {
        onClose();
      });
    }
  };

  const steps = getSteps({
    formRequest,
    setFormRequest,
  });

  const handleNextStep = () => {
    if (!steps[activeStep].validate()) {
      return;
    }

    setActiveStep(activeStep + 1);
  };

  return (
    <Dialog
      PaperProps={{ className: classes.formDialog }}
      open={open}
      onClose={onClose}
      aria-labelledby="add-form-dialog-title"
      fullScreen={fullScreen}
      maxWidth="lg"
    >
      <DialogTitle id="add-form-dialog-title">
        {
          form
            ? 'Edit Form'
            : 'Add Form'
        }
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {
              steps.map(({
                label,
                optional,
                finishedPreview,
              }, index) => {
                const labelProps = {};
                if (optional) {
                  labelProps.optional = <Typography variant="caption">Optional</Typography>;
                }

                const preview = finishedPreview();

                return (
                  <Step key={label}>
                    <StepLabel {...labelProps}>
                      {label}
                      {
                        activeStep > index
                          && preview.length > 0
                          && `: ${preview}`
                      }
                    </StepLabel>
                    {
                      activeStep === index
                        && (
                          <div className={classes.step}>
                            {
                              steps[activeStep].content
                            }
                          </div>
                        )
                    }
                  </Step>
                );
              })
            }
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button aria-label="cancel" onClick={onClose} size="small" disabled={isAddingForm || isEditingForm}>Cancel</Button>
        <Button disabled={activeStep === 0} onClick={() => { setActiveStep(activeStep - 1); }} size="small">
          Back
        </Button>
        {
          activeStep === (steps.length - 1)
            ? (
              <Button size="small" onClick={onSaveForm} color="primary" variant="contained" disabled={isAddingForm || isEditingForm}>
                {
                  (isAddingForm || isEditingForm)
                    && <CircularProgress size={20} className={classes.loadingButtonProgress} />
                }
                Save
              </Button>
            )
            : (
              <Button variant="contained" color="primary" onClick={handleNextStep} size="small">
                Next
              </Button>
            )
        }
      </DialogActions>
    </Dialog>
  );
};

AddEditFormModalComponent.propTypes = {
  form: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  fullScreen: PropTypes.bool,
};
AddEditFormModalComponent.defaultProps = {
  form: null,
  open: false,
  onClose: () => {},
  fullScreen: true,
};

export default (withMobileDialog({ breakpoint: 'sm' })(AddEditFormModalComponent));
